import React, { useMemo } from 'react';

import { LocalTable as PrismLocalTable } from '@nutanix-ui/prism-reactjs';
import type { LocalTableProps as PrismTableProps } from '@nutanix-ui/prism-reactjs/lib/components/v2/Tables/LocalTable';

import { getFitHeightStructure } from './fitHeight';
import type { FitHeightOffset } from './fitHeight';
import { useTableColumnResize } from './useTableColumnResize';

export type TableProps<T> = Omit<PrismTableProps, 'dataSource'> & {
  dataSource: T[];
  fitHeight?: FitHeightOffset;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function LocalTable<T extends Record<string, any>>(props: TableProps<T>) {
  const { fitHeight, structure, ...rest } = props;
  const { columnWidths, handleChangeStructure } = useTableColumnResize(props);

  const sorters = useMemo(
    () =>
      props.sort?.sortable?.reduce((acc, columnName) => {
        acc[columnName] = (a: T, b: T) => {
          const valA = a[columnName];
          const valB = b[columnName];
          if (typeof valA === 'number' && typeof valB === 'number') {
            return valA - valB;
          }
          return valA.localeCompare(valB);
        };
        return acc;
      }, {}),
    [props.sort?.sortable]
  );

  const tableStructure = getFitHeightStructure(structure, fitHeight);

  return (
    <PrismLocalTable
      {...rest}
      structure={{ columnResize: true, ...tableStructure, columnWidths }}
      onChangeStructure={handleChangeStructure}
      sorters={sorters}
    />
  );
}
